// Labels store - stores API values in memory & localStorage for Web Admin

const labelsStore: Record<string, string> = {};

// Default / fallback labels matching APK
const defaultLabels: Record<string, string> = {
  NCA: "NCA",
  MSL: "MSL",
  OutletOrgCode: "Outlet Org Code",
  Outlet: "Outlet",
  Me: "Me",
  Credited: "Credited",
  Debited: "Distributed",
  EndMyDay: "End my day",
  OVP: "OVP",
  Input: "Input",
  RECIEPT: "RECIEPT",
  ISSUE: "ISSUE",
  PTR: "PTR",
  Feedback: "Feedback",
};

const LABELS_STORAGE_KEY = "@app_labels";

/**
 * Helper to match capitalization case of source string onto target string
 */
function matchCase(source: string, target: string): string {
  if (!source || !target) return target;
  if (source === source.toUpperCase()) return target.toUpperCase();
  if (source === source.toLowerCase()) return target.toLowerCase();
  if (source[0] === source[0].toUpperCase()) {
    return target.charAt(0).toUpperCase() + target.slice(1);
  }
  return target;
}

/**
 * Get label value by key with dynamic case and plural ('s') support.
 * Examples:
 * - getLabel("Outlet") -> returns "Customer" (or API label)
 * - getLabel("Outlets") -> returns "Customers"
 * - getLabel("outlet") -> returns "customer"
 * - getLabel("NCA") -> returns "Field Activity" (or API label)
 * - getLabel("NCAs") -> returns "Field Activities"
 */
export function getLabel(key: string): string {
  if (!key) return "";

  // Helper to normalize keys (lowercase and remove non-alphanumeric chars)
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const normKey = norm(key);

  // 1. Direct exact match in labelsStore or defaultLabels
  if (labelsStore[key]) {
    return labelsStore[key];
  }
  if (defaultLabels[key]) {
    return defaultLabels[key];
  }

  // 2. Case-insensitive & normalized match in labelsStore
  const storeKeyMatch = Object.keys(labelsStore).find(
    (k) => k.toLowerCase() === key.toLowerCase() || norm(k) === normKey
  );
  if (storeKeyMatch && labelsStore[storeKeyMatch]) {
    return matchCase(key, labelsStore[storeKeyMatch]);
  }

  // Normalized match in defaultLabels
  const defaultKeyMatch = Object.keys(defaultLabels).find(
    (k) => k.toLowerCase() === key.toLowerCase() || norm(k) === normKey
  );
  if (defaultKeyMatch && defaultLabels[defaultKeyMatch]) {
    return matchCase(key, defaultLabels[defaultKeyMatch]);
  }

  // 3. Smart Plural handling for keys ending in 's' or 'S' (e.g. "Outlets", "NCAs", "customers")
  if (key.length > 1 && (key.endsWith("s") || key.endsWith("S"))) {
    const baseKey = key.slice(0, -1);
    const baseValue = getLabel(baseKey);

    if (baseValue && norm(baseValue) !== norm(baseKey)) {
      let pluralResult = baseValue;
      if (baseValue.endsWith("y") || baseValue.endsWith("Y")) {
        pluralResult = baseValue.slice(0, -1) + (baseValue.endsWith("Y") ? "IES" : "ies");
      } else if (!baseValue.toLowerCase().endsWith("s")) {
        pluralResult = baseValue + (baseValue === baseValue.toUpperCase() ? "S" : "s");
      }
      return matchCase(key, pluralResult);
    }
  }

  return key;
}

/**
 * Set a label value in memory
 */
export function setLabel(key: string, value: string): void {
  if (key && value) {
    labelsStore[key] = value;
  }
}

/**
 * Save all labels to localStorage
 */
export function saveLabelsToStorage(): void {
  try {
    localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(labelsStore));
  } catch (error) {
    console.error("Error saving labels to storage:", error);
  }
}

/**
 * Load persisted labels from localStorage on startup
 */
export function loadPersistedLabels(): boolean {
  try {
    const stored = localStorage.getItem(LABELS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      Object.assign(labelsStore, parsed);
      return true;
    }
  } catch (error) {
    console.error("Error loading persisted labels:", error);
  }
  return false;
}

// Auto-load on module import
loadPersistedLabels();

/**
 * Clear all persisted labels (on logout)
 */
export function clearPersistedLabels(): void {
  try {
    Object.keys(labelsStore).forEach((key) => delete labelsStore[key]);
    localStorage.removeItem(LABELS_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing labels:", error);
  }
}

/**
 * Process getTranslatedLabels API response and store labels
 * Supports array of label objects, single label object, or key-value dictionary
 */
export function updateLabelsFromAPI(response: any): void {
  if (!response) return;

  const rawData = response?.data?.data ?? response?.data ?? response;

  if (rawData?.label_key && rawData?.label_value) {
    setLabel(rawData.label_key, rawData.label_value);
  }

  if (Array.isArray(rawData)) {
    rawData.forEach((item: any) => {
      if (item?.label_key && item?.label_value) {
        setLabel(item.label_key, item.label_value);
      } else if (item?.key && item?.value) {
        setLabel(item.key, item.value);
      }
    });
  } else if (typeof rawData === "object" && rawData !== null) {
    Object.keys(rawData).forEach((k) => {
      if (typeof rawData[k] === "string") {
        setLabel(k, rawData[k]);
      }
    });
  }

  saveLabelsToStorage();
}

export const NCA = "NCA";
